import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContractSignPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [member, setMember] = useState(null);
  const [languages, setLanguages] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState('pt');
  const [translatedHtml, setTranslatedHtml] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [facialImage, setFacialImage] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [validationCode, setValidationCode] = useState('');

  useEffect(() => {
    loadContract();
  }, [token]);

  const loadContract = async () => {
    try {
      const res = await axios.get(`${API}/contracts/sign/${token}`);
      setContract(res.data.contract);
      setMember(res.data.member);
      setLanguages(res.data.languages);
      
      if (res.data.contract.signed) {
        setSigned(true);
        setValidationCode(res.data.contract.digital_signature?.validation_code || '');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Link inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async (lang) => {
    if (lang === 'pt') {
      setTranslatedHtml(null);
      setSelectedLanguage('pt');
      return;
    }
    
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/contracts/${contract.id}/translate`, {
        language: lang
      });
      setTranslatedHtml(res.data.html);
      setSelectedLanguage(lang);
    } catch (err) {
      alert('Erro ao traduzir. Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  const handleSign = async () => {
    if (!facialImage) {
      alert('Tire uma foto facial para assinar o contrato');
      return;
    }

    setSigning(true);
    try {
      let geolocation = {};
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          geolocation = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy
          };
        } catch (e) {
          console.log('Geolocalização não disponível');
        }
      }

      const res = await axios.post(`${API}/contracts/sign/${token}/confirm`, {
        facial_image: facialImage.split(',')[1],
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        },
        geolocation
      });

      if (res.data.success) {
        setSigned(true);
        setValidationCode(res.data.validation_code);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao assinar');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Carregando contrato...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center max-w-md">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl text-red-400 font-semibold mb-2">Link Inválido</h2>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center max-w-md"
        >
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl text-emerald-400 font-semibold mb-2">Contrato Assinado!</h2>
          <p className="text-white/60 mb-4">Assinatura digital registrada com sucesso</p>
          
          <div className="bg-black/30 rounded-xl p-4 mb-4">
            <p className="text-xs text-white/40 mb-1">Código de Validação</p>
            <p className="text-lg text-white font-mono font-bold">{validationCode}</p>
          </div>
          
          <p className="text-white/40 text-xs">
            Guarde este código para verificação futura
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-white font-semibold leading-tight">WClub</h1>
              <p className="text-white/50 text-xs truncate">Contrato de Assinatura</p>
            </div>
          </div>

          {/* Sign action — directs the member straight to the signature section */}
          {contract?.signed ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-semibold uppercase tracking-wider"
                 data-testid="contract-signed-badge">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden sm:inline">Assinado</span>
            </div>
          ) : (
            <button
              onClick={() => {
                const el = document.getElementById('signature-section');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="relative flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider transition active:scale-95 group"
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(168,85,247,0.18) 100%)',
                border: '1px solid rgba(196,181,253,0.45)',
                color: '#e9d5ff',
                boxShadow: '0 0 0 4px rgba(139,92,246,0.10), 0 4px 18px -4px rgba(139,92,246,0.45)',
              }}
              data-testid="contract-header-sign-btn"
              aria-label="Ir para assinatura digital"
            >
              {/* Pulse ring */}
              <span className="absolute inset-0 rounded-full pointer-events-none animate-ping opacity-40"
                    style={{ background: 'rgba(139,92,246,0.35)', animationDuration: '2.4s' }} />
              <svg className="w-4 h-4 sm:w-[18px] sm:h-[18px] relative" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
              <span className="relative">Assinar</span>
            </button>
          )}

          {/* Language Selector */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <svg className="hidden sm:block w-4 h-4 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
            <select
              value={selectedLanguage}
              onChange={(e) => handleTranslate(e.target.value)}
              disabled={translating}
              className="bg-white/10 border border-white/20 rounded-lg px-2 sm:px-3 py-1.5 text-white text-xs sm:text-sm outline-none"
            >
              {Object.entries(languages).map(([code, name]) => (
                <option key={code} value={code} className="bg-[#1a1a1a]">{name}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4">
        {/* Member Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
            {member?.name?.charAt(0) || 'M'}
          </div>
          <div>
            <p className="text-white font-semibold">{member?.name}</p>
            <p className="text-white/50 text-sm">Plano {contract?.plan}</p>
          </div>
        </div>

        {/* Translation Loading */}
        {translating && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-blue-400 text-sm">Traduzindo contrato...</p>
          </div>
        )}

        {/* Contract */}
        <div className="bg-white rounded-xl overflow-hidden mb-4 shadow-2xl">
          <iframe
            srcDoc={translatedHtml || contract?.html_content}
            className="w-full h-[500px]"
            title="Contrato"
          />
        </div>

        {/* Signature Section */}
        <div id="signature-section" className="bg-white/5 border border-white/10 rounded-xl p-6 scroll-mt-24">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Assinatura Digital
          </h3>
          
          <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4 mb-4">
            <p className="text-violet-300 text-sm mb-1">✓ Reconhecimento facial (Face ID)</p>
            <p className="text-violet-300 text-sm mb-1">✓ Timestamp e IP registrados</p>
            <p className="text-violet-300 text-sm">✓ Hash de validação único</p>
          </div>

          {/* Facial Capture */}
          <div className="border-2 border-dashed border-white/20 rounded-xl overflow-hidden mb-4">
            {facialImage ? (
              <div className="relative">
                <img src={facialImage} alt="Foto facial" className="w-full h-48 object-cover" />
                <button
                  onClick={() => setFacialImage(null)}
                  className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-emerald-500 rounded text-white text-xs">
                  ✓ Foto capturada
                </div>
              </div>
            ) : (
              <label className="block cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => setFacialImage(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <div className="p-8 text-center hover:bg-white/5 transition">
                  <svg className="w-16 h-16 mx-auto text-violet-400/50 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-white/60">Tire uma selfie para assinar</p>
                  <p className="text-white/30 text-xs mt-1">Clique para abrir a câmera</p>
                </div>
              </label>
            )}
          </div>

          <button
            onClick={handleSign}
            disabled={!facialImage || signing}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2 ${
              facialImage && !signing
                ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:opacity-90'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
          >
            {signing ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Assinando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Assinar Contrato
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-white/30 text-xs mt-6">
          WClub • Winners Co. LTDA • CNPJ: 57.110.169/0001-11
        </p>
      </div>
    </div>
  );
};

export default ContractSignPage;
